import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import { Page, Pageable } from 'src/app/utils/pageable';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import { AppEventsService } from '../../../services/app-events.service';
import {environment} from "../../../../../environments/environment";
import {WebSocketService} from "../../../services/web-socket.service";
import { ProductContextService } from 'src/app/col/services/product-context.service';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { AuthorizationServiceProvider } from 'src/app/auth_profile/services/authorization-service.provider';
import * as LessonsV3 from '../../../model/v3/lessons';
import { ColNativeV3Api } from '../../../services/api/v3/col-native-v3-api';
import { LessonBundlesUtilsV3 } from '../../../utils/v3/lesson-bundles-utils';

@Component({
    selector: 'app-student-credits',
    templateUrl: './student-credits.component.html',
    styleUrls: ['./student-credits.component.scss'],
    standalone: false
})
export class StudentCreditsComponent implements OnInit, OnDestroy {
  bundlesPage: Page<LessonsV3.LessonBundle>;
  bundles: LessonsV3.LessonBundle[];
  pageable: Pageable = Pageable.of(0, 5, ['lb.date,desc', 'lb.id,desc']);
  creditsTotal: number;
  private _studentId: number;
  private schoolId: number;
  _langCode: string;
  isLoading = true;
  wsSubscription: Subscription;
  scheduleSubscription: Subscription;
  productContextSubscription: Subscription;

  @Input() set studentId(id: number) {
    this._studentId = id;
    this.listenToWebSocketCreditsUpdate();
    this.reload();
  }
  @Input() set langCode(langCode: string) {
    this._langCode = langCode;
    this.reload();
  }

  constructor(
    private appEvents: AppEventsService,
    private spinner: SpinnerService,
    private webSocket: WebSocketService,
    private productContextService: ProductContextService,
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
  ) {}

  ngOnInit(): void {
    this.auth.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
        this.reload();
      })
    ).subscribe();
    this.scheduleSubscription = this.appEvents.creditsUpdate.subscribe((_) => {
      this.reload();
    });
    this.productContextSubscription = this.productContextService.productContext.subscribe(() => this.reload());
  }

  ngOnDestroy(): void {
    this.scheduleSubscription?.unsubscribe();
    this.wsSubscription?.unsubscribe();
    this.productContextSubscription?.unsubscribe();
  }

  private reload(): void {
    this.loadBundles(true);
  }

  private loadBundles(refreshCreditsTotal: boolean): void {
    if (!this.schoolId || !this._studentId) {
      return;
    }
    this.spinner
      .trace(
        this.provider.colV3().pipe(
          switchMap((api) =>
            forkJoin({
              page: this.fetchBundles(api, this.pageable),
              // the counter has to cover every bundle, not just the page being displayed
              allBundles: refreshCreditsTotal
                ? this.fetchBundles(api, LessonBundlesUtilsV3.allBundlesPageable())
                : of(null)
            })
          )
        )
      )
      .subscribe(({page, allBundles}) => {
        // the page itself feeds the paginator, which stays hidden without it
        this.bundlesPage = page;
        this.bundles = page?.content;
        if (refreshCreditsTotal) {
          this.creditsTotal = LessonBundlesUtilsV3.sumAvailableCredits(allBundles);
          this.appEvents.credits(this._langCode).next(this.creditsTotal);
        }
        this.isLoading = false;
      });
  }

  private fetchBundles(api: ColNativeV3Api, pageable: Pageable): Observable<Page<LessonsV3.LessonBundle>> {
    return api.getStudentBundles(
      this.schoolId,
      this._studentId,
      pageable,
      this._langCode,
      true
    );
  }

  getBundles() {
    return this.bundles;
  }

  onPageChange($event) {
    this.pageable = $event;
    this.loadBundles(false);
  }

  listenToWebSocketCreditsUpdate(): void {
    if (!this._studentId) {
      return;
    }
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint)
      .pipe(
        switchMap(connection => connection.subscribe(
            `/col/student/${this._studentId}/creditsUpdate`
          )
        )
      ).subscribe(() => this.reload());
  }
}
